import { createTheme, ThemeProvider } from "@material-ui/core";
import { useEffect } from "react";
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";
import App from "./app/App";
import * as Components from "./containers";
import OAuth2RedirectHandler from "./user/oauth2/OAuth2RedirectHandler";
import { getCurrentUser } from "./util/APIUtils";
import { getObject, getRolePermisionRoutes, isLoggedIn, saveObject } from "./utils";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
const UserRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) => {
        let isValidRoute = getRolePermisionRoutes().includes(rest.path);
        if (rest.path === "/welcome") {
          isValidRoute = true;
        }
        const secoundaryRoute = getRolePermisionRoutes()[0] || "/welcome";
        return isLoggedIn() ? (
          isValidRoute ? (
            <Components.UserMain {...props} {...rest}>
              <Component {...props} />
            </Components.UserMain>
          ) : (
            <Components.UserMain {...props} {...rest}>
              <Redirect to={secoundaryRoute} />
            </Components.UserMain>
          )
        ) : (
          <Redirect to="/login" />
        );
      }}
    />
  );
};

const PublicRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isLoggedIn() ? <Redirect to="/" /> : <Component {...props} />
      }
    />
  );
};
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#313C4C",
      dark: "#0a2a47",
      light: "#e6f7ff",
    },
    action: {
      hover: "rgba(16, 63, 107, 0.08)",
    },
  },

  typography: {
    fontWeightLight: 300,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    body1: { fontWeight: 500 },
    body2: { fontWeight: 500 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
    caption: { fontWeight: 500 },
    overline: { fontWeight: 500 },
    button: { fontWeight: 500 },
  },

  props: {
    MuiSwitch: {
      color: "primary",
    },
    MuiButtonBase: {
      disableRipple: true,
      disableTouchRipple: true,
    },
    MuiModal: { disableScrollLock: true },
    MuiPopover: { disableScrollLock: true },
    MuiMenu: { disableScrollLock: true },
    MuiDialog: { disableScrollLock: true },
  },

  overrides: {
    MuiSvgIcon: {
      root: {
        color: "#313C4C",
      },
    },

    MuiTypography: {
      root: { fontWeight: 500 },
      body1: { fontWeight: 500 },
      body2: { fontWeight: 500 },
    },

    MuiInputBase: {
      root: { fontWeight: 500 },
      input: { fontWeight: 500 },
    },

    MuiTableCell: {
      root: { fontWeight: 500 },
      body: { fontWeight: 500 },
      head: { fontWeight: 600 },
    },

    MuiMenuItem: {
      root: { fontWeight: 500 },
    },

    MuiListItemText: {
      primary: { fontWeight: 500 },
      secondary: { fontWeight: 500 },
    },

    MuiFormLabel: {
      root: { fontWeight: 500 },
    },

    MuiIconButton: {
      root: {
        "&:hover": {
          backgroundColor: "#e6f7ff",
          color: "#313C4C",
          "& .MuiSvgIcon-root": {
            color: "#313C4C",
          },
        },
      },
    },

    MuiSwitch: {
      switchBase: {
        color: "#cccccc",
        "&$checked": {
          color: "#313C4C",
        },
        "&$checked + $track": {
          backgroundColor: "#313C4C",
        },
      },
      track: {
        backgroundColor: "#ccc",
      },
    },

    MuiButton: {
      root: { fontWeight: 500 },
      textPrimary: {
        "&:hover": {
          backgroundColor: "#e6f7ff",
        },
      },
      outlinedPrimary: {
        "&:hover": {
          backgroundColor: "#e6f7ff",
        },
      },
    },
  },
});

function Routes() {
  const fetchCurrentUser = async () => {
    const isAuthenticated = isLoggedIn();
    const auth = await getObject("auth");

    if (isAuthenticated) {
      const user = await getCurrentUser();
      if (user?.success) {
        saveObject("auth", { ...auth, ...user?.data });
      }
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  return (
    <ThemeProvider theme={customTheme}>
      <Router>
        <PwaInstallPrompt />
        <Switch>
          <UserRoute
            exact
            path="/"
            component={Components.Dashboard}
            routeName="Admin Dashboard"
          />
          <UserRoute
            exact
            path="/user-list"
            component={Components.UserList}
            routeName="User List"
          />
          <UserRoute
            exact
            path="/administration"
            component={Components.Administration}
            routeName="Administration"
          />
          <UserRoute
            exact
            path="/knowledge-partner"
            component={Components.KnowledgePartner}
            routeName="Knowledge Partner"
          />
          <UserRoute
            exact
            path="/admin/knowledge-partners"
            component={Components.AdminKnowledgePartners}
            routeName="All Knowledge Partners"
          />
          <UserRoute
            exact
            path="/risk-management"
            component={Components.RiskManagement}
            routeName="Risk Management"
          />
          <UserRoute
            exact
            path="/ship-management"
            component={Components.ShipManagement}
            routeName="Ship Management"
          />
          <UserRoute
            exact
            path="/port-service"
            component={Components.PortService}
            routeName="Port Service"
          />
          <UserRoute
            exact
            path="/fuel-supply"
            component={Components.FuelSupply}
            routeName="Fuel Supply"
          />
          <UserRoute
            exact
            path="/accounting"
            component={Components.Accounting}
            routeName="Accounting"
          />
          <UserRoute
            exact
            path="/market-curve-analysis"
            component={Components.UserDashboard}
            routeName="Market Curve Analysis"
          />
          <UserRoute
            exact
            path="/my-market-curve-analysis"
            component={Components.MyMarketIndex}
            routeName="My Market Curve Analysis"
          />
          <UserRoute
            exact
            path="/welcome"
            component={Components.Welcome}
            routeName="Welcome"
          />
          <UserRoute
            exact
            path="/userOrganization"
            component={Components.UserMgntOrg}
            routeName="User Managment Organization"
          />
          <UserRoute
            exact
            path="/addressBook"
            component={Components.AddressBook}
            routeName="Address Book"
          />
          <UserRoute
            exact
            path="/cargo-cargo-matrix"
            component={Components.CargoCargoMatrix}
            routeName="Cargo Cargo Estimation"
          />
          <UserRoute
            exact
            path="/cargo-analysis"
            component={Components.CargoAnalysis}
            routeName="Cargo Analysis"
          />
          <UserRoute
            exact
            path="/vessel-vessel-matrix"
            component={Components.VesselVesselMatrix}
            routeName="Vessel Vessel Estimation"
          />
          <UserRoute
            exact
            path="/vessel-vessel/estimation/:id"
            component={Components.VesselVesselMatrixMain}
            routeName="Vessel Vessel Estimation"
          />
          <UserRoute
            exact
            path="/cargo-analysis/estimation/:id"
            component={Components.CargoAnalysisMatrix}
            routeName="Cargo Analysis Estimation"
          />
          <UserRoute
            exact
            path="/estimation-analysis/estimation/:vesselAnalysisId"
            component={Components.VesselAnalysisEstimation}
            routeName="Estimation analysis"
          />
          <UserRoute
            exact
            path="/estimation-analysis"
            component={Components.VesselAnalysisListing}
            routeName="Estimation Analysis"
          />
          <UserRoute
            exact
            path="/distance-matrix"
            component={Components.DistanceMatrix}
            routeName="Distance Estimation"
          />
          <UserRoute
            exact
            path="/port-information"
            component={Components.PortInformation}
            routeName="Port Information"
          />

          <UserRoute
            exact
            path="/cargo-cargo/estimation/:id"
            component={Components.CargoCargoMatrixMain}
            routeName="Cargo Cargo Estimation"
          />
          <UserRoute
            exact
            path="/support"
            component={Components.Support}
            routeName="Support"
          />
          <UserRoute
            exact
            path="/messagebox"
            component={Components.Messagebox}
            routeName="Message Box"
          />
          <UserRoute
            exact
            path="/supportinformation"
            component={Components.Supportinformation}
            routeName="Support"
          />
          <UserRoute
            exact
            path="/vesselInformation"
            component={Components.VesselInformation}
            routeName="Vessel Information"
          />
          <UserRoute
            exact
            path="/organization"
            component={Components.SetupOrganization}
            routeName="Setup Organization"
          />
          <UserRoute
            exact
            path="/orders"
            component={Components.Orders}
            routeName="Orders"
          />
          <UserRoute
            exact
            path="/cargovessel"
            component={Components.Voyages}
            routeName="Cargo Vessel Estimation"
          />
          <UserRoute
            exact
            path="/all-estimation"
            component={Components.AllMatrix}
            routeName="All Estimation"
          />
          <UserRoute
            exact
            path="/profile"
            component={Components.UserProfile}
            routeName="Profile"
          />
          <UserRoute
            exact
            path="/calculator"
            component={Components.Calculator}
            routeName="Matrix"
          />
          <UserRoute
            exact
            path="/cargo-vessel-playground"
            component={Components.MartixCurve}
            routeName="Cargo Vessel Playground"
          />
          <UserRoute
            exact
            path="/positionReader"
            component={Components.EmailParser}
            routeName="Position Reader"
          />
          <UserRoute
            exact
            path="/vessel/:id"
            component={Components.SingleVessel}
            routeName="Vessel Information"
          />
          <UserRoute
            exact
            path="/saved/:id"
            // component={Components.SingleSavedResult}
            component={Components.SingleVesselCalcy}
            routeName="Cargo Vessel Estimation"
          />
          <UserRoute
            exact
            path="/saved-contracts/:id"
            component={Components.SavedContracts}
            routeName="Operation Estimate"
          />
          <UserRoute
            exact
            path="/saved-actual-contracts/:id"
            component={Components.SavedContracts}
            routeName="Operation Estimate"
          />
          <UserRoute
            exact
            path="/operation-calc/:id"
            component={Components.OperationCalc}
            routeName="Cargo Vessel Operations"
          />
          <UserRoute
            exact
            path="/cargo-cargo-calc/:id"
            component={Components.CargoCargoCalc}
            routeName="Cargo Cargo Operations"
          />
          <UserRoute
            exact
            path="/vessel-vessel-calc/:id"
            component={Components.VesselVesselCalc}
            routeName="Vessel Vessel Operations"
          />
          <UserRoute
            exact
            path="/ops-calc/:id"
            component={Components.OperationCalc}
            routeName="Cargo Vessel Operations"
          />
          <UserRoute
            exact
            path="/saved-operation/:id"
            component={Components.SavedOperation}
            routeName="Saved Operation Results"
          />
          <UserRoute
            exact
            path="/saved-revised-contracts/:id"
            component={Components.SavedOperation}
            routeName="Saved Operation Results"
          />
          {/* <UserRoute
          exact
          path="/singlevessel/:queryid/calculater/:vesselid"
          component={Components.SingleVesselCalcy}
          routeName="Single Vessel Cargo Simulator"
        /> */}
          <UserRoute
            exact
            path="/saved"
            component={Components.SavedResults}
            routeName="Saved Results"
          />
          <UserRoute
            exact
            path="/map/:id"
            component={Components.ShowMap}
            routeName="Map"
          />
          <UserRoute
            exact
            path="/vesselIndexMap/:indexId"
            component={Components.VesselMapPage}
            routeName="Map"
          />
          <UserRoute
            exact
            path="/vesselManager"
            component={Components.Fleet}
            routeName="Vessel Manager"
          />
          <UserRoute
            exact
            path="/contracts"
            component={Components.Contracts}
            routeName="Contracts"
          />
          <UserRoute
            exact
            path="/vessel-vessel-contract/:id"
            component={Components.VesselVesselContract}
            routeName="Vessel Vessel Contracts"
          />
          <UserRoute
            exact
            path="/cargo-cargo-contract/:id"
            component={Components.CargoCargoContract}
            routeName="Cargo Cargo Contracts"
          />
          <UserRoute
            exact
            path="/operations"
            component={Components.Operations}
            routeName="Operations"
          />
          <UserRoute
            exact
            path="/voyage-list"
            component={Components.VoyageList}
            routeName="Voyage List"
          />
          <UserRoute
            exact
            path="/smart-laytime"
            component={Components.ElectricLaytime}
            routeName="Smart Laytime"
          />
          <UserRoute
            exact
            path="/smart-sof"
            component={Components.ElectricSof}
            routeName="Smart Statement of Fact"
          />
          <PublicRoute exact path="/login" component={Components.Auth} />
          <Redirect from="/forgotPassword" to="/login" />
          <Redirect from="/signUp" to="/login" />
          <PublicRoute
            exact
            path="/resetPassword"
            component={Components.ResetPassword}
          />
          <PublicRoute exact path="/google" component={App} />
          <PublicRoute
            exact
            path="/oauth2/redirect"
            component={OAuth2RedirectHandler}
          />
          <PublicRoute exact path="/profile" component={App} />
        </Switch>
      </Router>
    </ThemeProvider>
  );
}

export default Routes;
